/**
 * nav — single source of truth for the app's navigation tabs. Both the sidebar
 * and the Settings tab-visibility selector derive from NAV_TABS, so adding a tab
 * here both shows it in the sidebar and gives it a per-tab access control.
 *
 * These states are a CLIENT-SIDE visibility/UX layer, not the security boundary:
 * every `/api/data/*` write requires a valid session cookie server-side
 * (requireAuth); the client merely hides edit controls so logged-out visitors
 * aren't offered an action that would 401.
 */
import type { LucideIcon } from 'lucide-react';
import {
  LineChart, Receipt, PenLine, ListChecks,
  NotebookPen, FlaskConical, Dice5, TrendingUp, ScatterChart, Zap,
  Lightbulb, BarChart3, Network, Filter, Sparkles, Camera, CandlestickChart,
  Settings as SettingsIcon, ShieldCheck, Share2,
} from 'lucide-react';

/**
 * Per-tab access (is the tab listed/openable to the public, and does changing
 * its data require a login):
 *   'public'    — listed for everyone; anyone may open and edit.
 *   'protected' — listed and openable, but read-only for logged-out visitors.
 *   'hidden'    — unlisted but reachable by direct link; edit controls show.
 *   'login'     — unlisted and route-gated; logged-out visitors are sent to /login.
 * For a signed-in owner every tab is listed and fully editable.
 */
export type TabAccess = 'public' | 'protected' | 'hidden' | 'login';

export interface NavTab {
  /** Stable key used in settings.tabVisibility and as the React key. */
  key: string;
  /** Router path (must match a <Route path> in main.tsx). */
  to: string;
  label: string;
  desc: string;
  icon: LucideIcon;
  section: string;
  /**
   * Tabs that must always require a login before their data can change. The
   * selector offers only 'protected'/'login', and resolveTabAccess upgrades any
   * stale no-login state to 'login'.
   */
  forceLogin?: boolean;
  /** Tabs whose access is fixed and not user-editable. */
  lockAccess?: TabAccess;
  /**
   * Always render this tab in the sidebar, even for signed-out visitors. The
   * route guard still enforces access, so an always-shown login tab appears as a
   * visible "sign in to use" entry point. Independent of lockAccess / forceLogin.
   */
  alwaysShow?: boolean;
  /** Effective access when the user hasn't chosen one. */
  defaultAccess: TabAccess;
}

/** Sidebar / settings section render order. */
export const SECTION_ORDER = ['Overview', 'Trading', 'Analysis', 'Research', 'Portfolio', 'Config'] as const;

export const NAV_TABS: NavTab[] = [
  { key: 'performanceSummary', to: '/performance-summary', label: 'Performance Summary', desc: 'Portfolio overview with chart, KPIs, holdings, plus Market Calendar (dividends, macro, earnings, news), Market Themes, Global Indexes, and Watchlist subtabs', icon: LineChart, section: 'Overview', defaultAccess: 'public' },
  { key: 'tax', to: '/tax', label: 'Tax', desc: 'Cost basis, realized P&L, and tax-loss harvest opportunities', icon: Receipt, section: 'Overview', defaultAccess: 'public' },
  { key: 'journal', to: '/investment-thesis', label: 'Investment Thesis', desc: 'Personal investment research notes', icon: NotebookPen, section: 'Overview', defaultAccess: 'public' },
  { key: 'analytics', to: '/analytics', label: 'Analytics', desc: 'Sector allocation, performance breakdown', icon: BarChart3, section: 'Overview', defaultAccess: 'public' },

  { key: 'logTrade', to: '/log-trade', label: 'Log Trade', desc: 'Record buy and sell transactions, plus Import and Manual changes subtabs', icon: PenLine, section: 'Trading', forceLogin: true, defaultAccess: 'login' },
  { key: 'tradeHistory', to: '/trade-history', label: 'Trade History', desc: 'Full list of all past trades', icon: ListChecks, section: 'Trading', defaultAccess: 'public' },
  { key: 'tradingTools', to: '/trading-tools', label: 'Trading Tools', desc: 'TA charting — candles, indicators, Weis Wave, drawings, swing-strategy signals and screener', icon: CandlestickChart, section: 'Trading', defaultAccess: 'public' },

  { key: 'backtest', to: '/backtest', label: 'Backtest', desc: 'Replay strategies against historical prices', icon: FlaskConical, section: 'Research', defaultAccess: 'public' },
  { key: 'monteCarlo', to: '/monte-carlo', label: 'Monte Carlo', desc: 'Randomized return paths and distribution', icon: Dice5, section: 'Research', defaultAccess: 'public' },
  { key: 'factorRegression', to: '/factor-regression', label: 'Factor Regression', desc: 'Fama-French / multi-factor regression on returns', icon: TrendingUp, section: 'Research', defaultAccess: 'public' },
  { key: 'optimisation', to: '/optimisation', label: 'Optimisation', desc: 'Efficient frontier, mean-variance/CVaR/drawdown, Black-Litterman, rolling', icon: ScatterChart, section: 'Research', defaultAccess: 'public' },
  { key: 'tactical', to: '/tactical-allocation', label: 'Tactical Allocation', desc: 'Rules-based regime / momentum allocation', icon: Zap, section: 'Research', defaultAccess: 'public' },

  { key: 'correlations', to: '/asset-correlations', label: 'Asset Correlations', desc: 'Pairwise correlation matrix across holdings', icon: Network, section: 'Analysis', defaultAccess: 'public' },
  { key: 'fundScreener', to: '/fund-research', label: 'Fund Research', desc: 'Screen, rank, and analyse funds: performance, factor regressions, attribution, and manager analysis', icon: Filter, section: 'Analysis', defaultAccess: 'public' },
  { key: 'scenarioGenerator', to: '/scenario-generator', label: 'Scenario Generator', desc: 'Model hypothetical portfolio changes', icon: Sparkles, section: 'Analysis', defaultAccess: 'public' },

  { key: 'snapshots', to: '/snapshots', label: 'Snapshots', desc: 'Point-in-time portfolio captures', icon: Camera, section: 'Portfolio', defaultAccess: 'public' },

  { key: 'settings', to: '/settings', label: 'Settings', desc: 'Configure portfolio and visibility — always requires login', icon: SettingsIcon, section: 'Config', alwaysShow: true, forceLogin: true, lockAccess: 'login', defaultAccess: 'login' },
  { key: 'admin', to: '/admin', label: 'Admin', desc: 'Admin operations sheet — cloud sync, market data, cache', icon: ShieldCheck, section: 'Config', forceLogin: true, defaultAccess: 'login' },
  { key: 'publicView', to: '/public-view', label: 'Public View', desc: 'Share-friendly read-only portfolio page', icon: Share2, section: 'Config', lockAccess: 'public', defaultAccess: 'public' },
];

/**
 * Hub subtabs with their OWN access control, keyed to survive the page's move
 * under the Performance Summary hub: Market Themes keeps the legacy
 * 'thinkPieces' key, so a tabVisibility override saved when it was a
 * top-level tab still gates it here. Not part of NAV_TABS (they'd double-list
 * in the section nav) — resolved by routeTab() and offered in the Settings
 * visibility card alongside NAV_TABS.
 */
export const HUB_SUBTAB_TABS: NavTab[] = [
  { key: 'thinkPieces', to: '/performance-summary/market-themes', label: 'Market Themes', desc: 'Auto market brief, news feed, and your long-form commentary', icon: Lightbulb, section: 'Overview', defaultAccess: 'public' },
  // Gated 'login' and forceLogin: the panel exposes holdings, weights and (once
  // Rebalance lands) CGT estimates on an endpoint the public proxy can reach.
  // The hub itself defaults 'public', so without its own entry here this subtab
  // would inherit that and advertise the book to logged-out visitors.
  { key: 'projections', to: '/performance-summary/projections', label: 'Projections', desc: 'Forward simulation with copula tails, risk decomposition, views, rebalance and candidate screening', icon: Dice5, section: 'Overview', forceLogin: true, defaultAccess: 'login' },
];

const ACCESS_VALUES: readonly TabAccess[] = ['public', 'protected', 'hidden', 'login'];

/** No-login states: the tab's data can change without signing in. */
const NO_LOGIN_ACCESS: readonly TabAccess[] = ['public', 'hidden'];

export function isTabAccess(v: unknown): v is TabAccess {
  return typeof v === 'string' && (ACCESS_VALUES as readonly string[]).includes(v);
}

/**
 * Resolve a tab's effective access, applying its hard rules. lockAccess always
 * wins; a forceLogin tab can never resolve to a no-login state. THE function
 * every consumer calls, so the rules can't be bypassed by a bad settings blob.
 */
export function resolveTabAccess(
  tab: NavTab,
  tabVisibility?: Record<string, string> | null,
): TabAccess {
  if (tab.lockAccess) return tab.lockAccess;
  const stored = tabVisibility ? tabVisibility[tab.key] : undefined;
  let access: TabAccess = isTabAccess(stored) ? stored : tab.defaultAccess;
  if (tab.forceLogin && (NO_LOGIN_ACCESS as readonly string[]).includes(access)) {
    access = 'login';
  }
  return access;
}

/** The access options the Settings selector may offer for a given tab. */
export function allowedAccessOptions(tab: NavTab): TabAccess[] {
  if (tab.lockAccess) return [tab.lockAccess];
  if (tab.forceLogin) return ['protected', 'login'];
  return ['public', 'protected', 'hidden', 'login'];
}

/**
 * Whether the tab is listed in the sidebar for the given viewer. `alwaysShow` is
 * handled by the caller (AppShell), independent of this.
 */
export function showInSidebar(access: TabAccess, isAuthed: boolean): boolean {
  if (isAuthed) return true;
  return access === 'public' || access === 'protected';
}

/** Whether the route should render (vs. redirect). Only 'login' gates the route. */
export function canAccessRoute(access: TabAccess, isAuthed: boolean): boolean {
  if (access === 'login') return isAuthed;
  return true;
}

/** Whether edit controls should be suppressed: only 'protected' for logged-out viewers. */
export function readOnlyForViewer(access: TabAccess, isAuthed: boolean): boolean {
  return !isAuthed && access === 'protected';
}

/** Find the nav tab whose route exactly matches a pathname. */
export function tabByPath(pathname: string): NavTab | undefined {
  return NAV_TABS.find((t) => t.to === pathname);
}

/**
 * Resolve the tab governing ANY route, including hub subtabs: an exact
 * NAV_TABS match, else a gated hub subtab (HUB_SUBTAB_TABS), else the tab
 * whose route is a path prefix (so /performance-summary/dividends inherits
 * the performanceSummary tab's access). THE lookup RouteGate and
 * useTabReadOnly use — exact-only matching left every hub subtab ungated.
 */
export function routeTab(pathname: string): NavTab | undefined {
  return (
    tabByPath(pathname) ??
    HUB_SUBTAB_TABS.find((t) => t.to === pathname) ??
    // Hub subtabs with their own addressable inner panels (Projections uses
    // /performance-summary/projections/<panel>) must match by PREFIX too.
    // Exact-only matching let an inner panel fall through to the parent hub
    // tab, which defaults 'public' — so a login-gated subtab would have been
    // ungated at every URL except its bare one.
    HUB_SUBTAB_TABS.find((t) => pathname.startsWith(`${t.to}/`)) ??
    NAV_TABS.find((t) => pathname.startsWith(`${t.to}/`))
  );
}


/** Human-readable labels for each access state (used by the selector + UI). */
export const ACCESS_LABEL: Record<TabAccess, string> = {
  public: 'Public',
  protected: 'Requires login to change',
  hidden: 'Hidden',
  login: 'Hidden and requires login',
};
